import { useAppPageState } from '@/catalog-apps/components/AppPage2/useAppPageState'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { Switch } from '@/components/shadcn/switch'
import { AgentIcon, ApplicationIcon, OrganisationIcon, ProjectIcon, PWC_ID_CONFIG, WorkspaceIcon } from '@/const'
import { useLogout, useSessionStorage } from '@/hooks'
import { isTokenExpired } from '@/hooks/useTokenValidator'
import { getInitials } from '@/lib/utils'
import { useTheme } from '@/theme-provider'
import { hasApplicationsAccess, hasDocumentationAccess, hasSubscriptionAccess, isSuperUser } from '@/util'
import { IconBookOutline } from '@icons/IconBookOutline'
import { IconLoginOutline } from '@icons/IconLoginOutline'
import { IconLogoutOutline } from '@icons/IconLogoutOutline'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { IconMoonOutline } from '@icons/IconMoonOutline'
import { IconPinboardOutline } from '@icons/IconPinboardOutline'
import { IconV2SettingsOutline } from '@icons/IconV2SettingsOutline'
import { useState } from 'react'
import { Link, useMatch } from 'react-router'

export function UserWidget2() {
  const [token] = useSessionStorage('token', '')
  const [roles] = useSessionStorage('roles', 0)
  const [name] = useSessionStorage('name', '')
  const loggedIn = !!token && !isTokenExpired(token)
  const { logoutHandler } = useLogout()
  const [open, onOpenChange] = useState(false)
  const isCatalogAppOpen = useMatch('/apps/*')
  const { toggleIsConfiguringLayout } = useAppPageState()
  const { theme, setTheme } = useTheme()

  const closeMenu = () => onOpenChange(false)

  const avatar = (
    // <Button variant={name ? 'ghost' : 'secondary'} size="sm" className="rounded-full">
    <div className="flex items-center">
      <Button variant="ghost" size="icon" className="rounded-full">
        <IconMenuOutline className="size-5" />
      </Button>
      <Avatar>
        <AvatarFallback className="bg-accent text-accent-foreground">{getInitials(name)}</AvatarFallback>
      </Avatar>
    </div>
  )

  const loginButton = (
    <Button
      onClick={() => window.location.replace(PWC_ID_CONFIG.authUrl())}
      // variant="secondary"
      variant="ghost"
      size="sm"
      // className="rounded-full"
    >
      <IconLoginOutline className="size-5" />
      Login
    </Button>
  )

  return loggedIn ? (
    <>
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>{avatar}</DropdownMenuTrigger>
        <DropdownMenuContent>
          {loggedIn && (
            <>
              <DropdownMenuGroup>
                <Link className="text-foreground" to="/workspaces">
                  <DropdownMenuItem onSelect={closeMenu}>
                    <WorkspaceIcon className="mr-2 size-4" />
                    Workspaces
                  </DropdownMenuItem>
                </Link>
                {/* {isSuperUser(roles) && ( */}
                <Link className="text-foreground" to="/projects">
                  <DropdownMenuItem onSelect={closeMenu}>
                    <ProjectIcon className="mr-2 size-4" />
                    Projects
                  </DropdownMenuItem>
                </Link>
                {/* )} */}
                {isSuperUser(roles) && (
                  <Link className="text-foreground" to="/agent-studio/agents">
                    <DropdownMenuItem onSelect={closeMenu}>
                      <AgentIcon className="mr-2 size-4" />
                      Agent Studio
                    </DropdownMenuItem>
                  </Link>
                )}
                {hasSubscriptionAccess(roles) && (
                  <Link className="text-foreground" to="/subscriptions">
                    <DropdownMenuItem onSelect={closeMenu}>
                      <OrganisationIcon className="mr-2 size-4" />
                      Subscriptions
                    </DropdownMenuItem>
                  </Link>
                )}
                {hasApplicationsAccess(roles) && (
                  <Link className="text-foreground" to="/applications">
                    <DropdownMenuItem>
                      <ApplicationIcon className="mr-2 size-4" />
                      Applications
                    </DropdownMenuItem>
                  </Link>
                )}
                {hasDocumentationAccess(roles) && (
                  <Link className="text-foreground" to="/docs">
                    <DropdownMenuItem onSelect={closeMenu}>
                      <IconBookOutline className="mr-2 size-4" />
                      Documentation
                    </DropdownMenuItem>
                  </Link>
                )}
              </DropdownMenuGroup>

              <DropdownMenuSeparator />

              {isSuperUser(roles) && (
                <DropdownMenuGroup>
                  {/*<Link className="text-foreground" to="/chat-test?appKey=test">
                    <DropdownMenuItem onSelect={closeMenu}>
                      <IconBotOutline className="mr-2 size-4" />
                      Chat Test
                    </DropdownMenuItem>
                  </Link>*/}
                  <Link className="text-foreground" to="/platform/users">
                    <DropdownMenuItem>
                      <IconV2SettingsOutline className="mr-2 size-4" />
                      {/* IconBotOutline, IconQueryOutline, IconSettingOutline, IconWebsiteOutline */}
                      Platform
                    </DropdownMenuItem>
                  </Link>
                </DropdownMenuGroup>
              )}

              {isSuperUser(roles) && isCatalogAppOpen && (
                <DropdownMenuGroup>
                  <DropdownMenuItem onSelect={toggleIsConfiguringLayout}>
                    <IconPinboardOutline className="mr-2 size-4" />
                    Page Layout Editor
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <IconMoonOutline className="mr-2 size-4" />
                  Dark Mode
                  <Switch
                    className="ml-auto"
                    checked={theme === 'dark'}
                    onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                    aria-label="Toggle dark mode"
                  />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logoutHandler}>
                  <IconLogoutOutline className="mr-2 size-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  ) : (
    <div className="flex items-center justify-between gap-5">
      <div className="flex items-center">
        <IconMoonOutline className="mr-2 size-4" />
        <Switch
          className="ml-auto"
          checked={theme === 'dark'}
          onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
          aria-label="Toggle dark mode"
        />
      </div>
      {loginButton}
    </div>
  )
}
